import React, { useEffect, useState, useRef, lazy } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
export interface CarouselImage {
  src: string;
  label?: string;
}
interface CarouselProps {
  images: CarouselImage[];
  /** Tailwind width class for each slide, e.g. "w-[85%]" */
  slideWidthClass?: string;
  /** Color theme for arrows/dots: 'wine' (light bg) or 'light' (dark bg) */
  theme?: 'wine' | 'light';
  rounded?: string;
}
export function Carousel({
  images,
  slideWidthClass = 'w-[85%]',
  theme = 'wine',
  rounded = 'rounded-2xl'
}: CarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const updateActive = () => {
    const el = scrollRef.current;
    if (!el) return;
    const slide = el.querySelector<HTMLElement>('[data-slide]');
    if (!slide) return;
    const slideWidth = slide.offsetWidth + 16; // gap-4
    const idx = Math.round(el.scrollLeft / slideWidth);
    setActive(Math.max(0, Math.min(idx, images.length - 1)));
  };
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateActive, {
      passive: true
    });
    return () => el.removeEventListener('scroll', updateActive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length]);
  const scrollTo = (idx: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const slide = el.querySelector<HTMLElement>('[data-slide]');
    if (!slide) return;
    const slideWidth = slide.offsetWidth + 16;
    el.scrollTo({
      left: idx * slideWidth,
      behavior: 'smooth'
    });
  };
  const go = (dir: -1 | 1) => {
    const next = Math.max(0, Math.min(active + dir, images.length - 1));
    scrollTo(next);
  };
  const isLight = theme === 'light';
  const arrowBase = 'absolute top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full flex items-center justify-center shadow-md transition-all active:scale-95 disabled:opacity-0 disabled:pointer-events-none';
  const arrowColor = isLight ? 'bg-white/90 text-wine hover:bg-white' : 'bg-wine text-white hover:bg-wine-dark';
  return <div className="relative w-full" data-id="element-39">
      <div className="relative" data-id="element-40">
        <div ref={scrollRef} className="flex overflow-x-auto hide-scrollbar snap-x snap-mandatory gap-4 pb-4" data-id="element-41">
          {images.map((img, idx) => <div key={idx} data-slide className={`snap-start shrink-0 ${slideWidthClass} relative ${rounded} overflow-hidden shadow-md bg-black/5`} data-id="element-42">
              <div className="aspect-[4/3] w-full" data-id="element-43">
                <img src={img.src} alt={img.label || `Imagen ${idx + 1}`} loading="lazy" className="w-full h-full object-cover" data-id="element-44" />
              </div>
              {img.label && <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-lg text-xs font-bold text-wine shadow-sm" data-id="element-45">
                  {img.label}
                </div>}
            </div>)}
        </div>

        {/* Arrows */}
        <button type="button" aria-label="Imagen anterior" onClick={() => go(-1)} disabled={active === 0} className={`${arrowBase} ${arrowColor} left-2`} data-id="element-46">
          <ChevronLeft className="w-5 h-5" data-id="element-47" />
        </button>
        <button type="button" aria-label="Imagen siguiente" onClick={() => go(1)} disabled={active === images.length - 1} className={`${arrowBase} ${arrowColor} right-2`} data-id="element-48">
          <ChevronRight className="w-5 h-5" data-id="element-49" />
        </button>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-1.5 mt-1" data-id="element-50">
        {images.map((_, idx) => <button key={idx} type="button" aria-label={`Ir a la imagen ${idx + 1}`} onClick={() => scrollTo(idx)} className={`h-1.5 rounded-full transition-all ${idx === active ? isLight ? 'bg-white w-4' : 'bg-wine w-4' : isLight ? 'bg-white/30 w-1.5' : 'bg-wine/20 w-1.5'}`} data-id="element-51" />)}
      </div>
    </div>;
}